import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inr, formatDate } from "@/lib/utils_pharma";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  TrendingUp,
  Receipt,
  Pill,
  AlertTriangle,
  Clock,
  PackageSearch,
  ArrowRight,
  Printer,
} from "lucide-react";
import InvoicePreviewDialog from "@/components/InvoicePreviewDialog";
import DeleteBillDialog from "@/components/DeleteBillDialog";
import { useAuth } from "@/context/AuthContext";

function Stat({ label, value, icon: Icon, sub, testid, tone = "default" }) {
  const tones = {
    default: "bg-white",
    primary: "bg-primary text-primary-foreground",
    accent: "bg-accent text-accent-foreground",
  };
  return (
    <div className={`rounded-md border border-border p-5 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className={`text-xs uppercase tracking-widest ${tone === "default" ? "text-muted-foreground" : "opacity-80"}`}>{label}</div>
        <Icon size={16} className={tone === "default" ? "text-muted-foreground" : ""} />
      </div>
      <div className="mt-3 text-3xl font-semibold tabular-nums" style={{ fontFamily: "Chivo" }}>
        {value}
      </div>
      {sub && <div className={`mt-1 text-xs ${tone === "default" ? "text-muted-foreground" : "opacity-80"}`}>{sub}</div>}
    </div>
  );
}

function PaymentBadge({ mode }) {
  const styles = {
    Cash: "bg-emerald-50 text-emerald-700 border-emerald-200",
    UPI: "bg-blue-50 text-blue-700 border-blue-200",
    Card: "bg-purple-50 text-purple-700 border-purple-200",
    Split: "bg-amber-50 text-amber-700 border-amber-200",
    Credit: "bg-rose-50 text-rose-700 border-rose-200",
  };
  const colorClass = styles[mode] || "bg-secondary text-secondary-foreground border-border";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${colorClass}`}>
      {mode || "Cash"}
    </span>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [expiryReport, setExpiryReport] = useState(null);
  const [expiryDays, setExpiryDays] = useState("60");
  const [recentBills, setRecentBills] = useState([]);
  const [shopInfo, setShopInfo] = useState({});

  const [previewBill, setPreviewBill] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [billToDelete, setBillToDelete] = useState(null);

  const { user } = useAuth();
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("pharma_user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const currentUser = user || storedUser;
  const isAdmin = currentUser?.role === "admin";
  const isDeleteEnabled = process.env.REACT_APP_ENABLE_BILL_DELETE !== "false";
  const canDeleteBills = isAdmin && isDeleteEnabled;

  const loadData = async () => {
    try {
      const [statsRes, expiryRes, billsRes, shopRes] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/reports/stock/expiry", { params: { filter_type: "90", active_only: true } }),
        api.get("/bills", { params: { limit: 10 } }),
        api.get("/settings/shop").catch(() => ({ data: {} })),
      ]);
      setStats(statsRes.data);
      setExpiryReport(expiryRes.data);
      setRecentBills(billsRes.data || []);
      if (shopRes?.data) setShopInfo(shopRes.data);
    } catch (err) {
      console.error("Failed to load dashboard data", err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openDeleteModal = (bill) => {
    setBillToDelete(bill);
    setDeleteModalOpen(true);
  };

  const handleBillDeleted = () => {
    setPreviewBill(null);
    loadData();
  };

  const handlePaymentUpdated = (updatedBill) => {
    setPreviewBill(updatedBill);
    loadData();
  };

  // Cumulative upcoming expiry items: 0 <= diff_days <= selectedDays, excluding expired
  const filteredExpiryItems = useMemo(() => {
    const daysLimit = parseInt(expiryDays, 10);
    const items = (expiryReport?.items || []).filter((item) => {
      const diff = item.days_until_expiry;
      return diff !== undefined && diff !== null && diff >= 0 && diff <= daysLimit;
    });
    // Sort soonest expiry first
    return items.sort((a, b) => {
      if (a.expiry_date < b.expiry_date) return -1;
      if (a.expiry_date > b.expiry_date) return 1;
      return 0;
    });
  }, [expiryReport, expiryDays]);

  const lowStockItems = stats?.low_stock_items || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Overview</div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: "Chivo" }}>Dashboard</h1>
        </div>
        <div className="text-xs text-muted-foreground">
          {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </div>
      </div>

      {/* Row 1: KPI Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat
          testid="stat-today-sales"
          label="Today's Sales"
          value={inr(stats?.today_sales || 0)}
          sub={`${stats?.today_bill_count || 0} bills`}
          icon={TrendingUp}
          tone="primary"
        />
        <Stat
          testid="stat-bill-count"
          label="Bills Today"
          value={stats?.today_bill_count ?? "—"}
          sub="Invoices printed"
          icon={Receipt}
        />
        <Stat
          testid="stat-medicines"
          label="Medicines"
          value={stats?.total_medicines ?? "—"}
          sub="in catalogue"
          icon={Pill}
        />
        <Stat
          testid="stat-stock-value"
          label="Stock Value"
          value={inr(stats?.stock_value || 0)}
          sub="at cost"
          icon={PackageSearch}
        />
      </div>

      {/* Row 2: Low Stock Alerts & Expiring Stock (Equal fixed height h-[365px] + internal scroll) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        {/* Low Stock Alerts */}
        <Card data-testid="low-stock-card" className="border-border rounded-md shadow-none flex flex-col justify-between h-[365px]">
          <CardHeader className="border-b border-border py-2.5 px-5 shrink-0">
            <CardTitle className="flex items-center justify-between text-base h-7">
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} className="text-amber-500" />
                <span>Low Stock Alerts</span>
              </div>
              <Badge variant="secondary" className="tabular-nums font-semibold" data-testid="low-stock-total-badge">
                {stats?.low_stock_count ?? 0}
              </Badge>
            </CardTitle>
          </CardHeader>

          {/* Scrollable list container (~5 visible rows) */}
          <div className="h-[268px] overflow-y-auto subtle-scrollbar divide-y divide-border">
            {(!stats || lowStockItems.length === 0) ? (
              <div className="h-full flex items-center justify-center p-6 text-sm text-muted-foreground text-center">
                All medicines are well stocked.
              </div>
            ) : (
              lowStockItems.map((m) => (
                <div key={m.id || m.name} className="flex items-center justify-between px-5 py-2.5 text-sm data-row">
                  <div className="flex-1 min-w-0 pr-3">
                    <div className="font-medium text-foreground truncate">{m.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{m.manufacturer || "—"}</div>
                  </div>
                  <div className="text-right tabular-nums shrink-0">
                    <div className={`font-semibold ${m.stock === 0 ? "text-destructive" : "text-amber-600"}`}>
                      {m.stock === 0 ? "Out of Stock" : `${m.stock} left`}
                    </div>
                    <div className="text-xs text-muted-foreground">threshold {m.low_stock_threshold ?? 10}</div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="border-t border-border px-5 py-2.5 bg-muted/20 flex justify-end shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-primary font-medium hover:text-primary/80 hover:bg-transparent p-0 h-auto gap-1"
              onClick={() => navigate("/reports?tab=low-stock")}
              data-testid="view-all-low-stock"
            >
              View All <ArrowRight size={13} />
            </Button>
          </div>
        </Card>

        {/* Expiring Stock */}
        <Card data-testid="expiring-card" className="border-border rounded-md shadow-none flex flex-col justify-between h-[365px]">
          <CardHeader className="border-b border-border py-2.5 px-5 shrink-0">
            <CardTitle className="flex items-center justify-between text-base h-7">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-accent" />
                <span>Expiring Stock</span>
              </div>
              <div className="flex items-center gap-2">
                {/* Compact segmented range selector */}
                <div className="inline-flex items-center p-0.5 bg-muted/80 rounded-md border border-border">
                  {[
                    { id: "15", label: "15d" },
                    { id: "30", label: "30d" },
                    { id: "45", label: "45d" },
                    { id: "60", label: "60d" },
                  ].map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setExpiryDays(r.id)}
                      className={`px-2 py-0.5 text-xs font-medium rounded transition-all ${
                        expiryDays === r.id
                          ? "bg-white text-primary font-semibold shadow-xs"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`dashboard-expiry-${r.id}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <Badge variant="secondary" className="tabular-nums font-semibold" data-testid="expiring-total-badge">
                  {filteredExpiryItems.length}
                </Badge>
              </div>
            </CardTitle>
          </CardHeader>

          {/* Scrollable batch list container (~5 visible rows) */}
          <div className="h-[268px] overflow-y-auto subtle-scrollbar divide-y divide-border">
            {filteredExpiryItems.length === 0 ? (
              <div className="h-full flex items-center justify-center p-6 text-sm text-muted-foreground text-center">
                No batches expiring in {expiryDays} days.
              </div>
            ) : (
              filteredExpiryItems.map((m, idx) => (
                <div key={m.batch_id || m.id || idx} className="flex items-center justify-between px-5 py-2.5 text-sm data-row">
                  <div className="flex-1 min-w-0 pr-3">
                    <div className="font-medium text-foreground truncate">{m.medicine_name || m.name}</div>
                    <div className="text-xs text-muted-foreground truncate">Batch {m.batch_no || "—"}</div>
                  </div>
                  <div className="text-right tabular-nums shrink-0">
                    <div className="font-medium text-accent">{formatDate(m.expiry_date)}</div>
                    <div className="text-xs text-muted-foreground">stock {m.stock}</div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="border-t border-border px-5 py-2.5 bg-muted/20 flex justify-end shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-primary font-medium hover:text-primary/80 hover:bg-transparent p-0 h-auto gap-1"
              onClick={() => navigate(`/reports?tab=expiry&range=${expiryDays}`)}
              data-testid="view-all-expiring"
            >
              View All <ArrowRight size={13} />
            </Button>
          </div>
        </Card>
      </div>

      {/* Row 3: Recent Sales Bills (Latest 10 records, internal scrollable table with sticky header) */}
      <Card data-testid="recent-sales-card" className="border-border rounded-md shadow-none overflow-hidden">
        <CardHeader className="border-b border-border py-3 px-5 flex flex-row items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Receipt size={16} className="text-primary" />
            <CardTitle className="text-base font-semibold">Recent Sales Bills</CardTitle>
            <Badge variant="secondary" className="text-xs font-normal">Latest 10</Badge>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-primary font-medium hover:text-primary/80 hover:bg-transparent p-0 h-auto gap-1"
            onClick={() => navigate("/reports?tab=sales")}
            data-testid="view-all-sales-header"
          >
            View All <ArrowRight size={13} />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {recentBills.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">No sales bills recorded yet.</div>
          ) : (
            <div className="max-h-[260px] overflow-y-auto subtle-scrollbar">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10 shadow-xs border-b">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[180px] bg-white">Bill No.</TableHead>
                    <TableHead className="w-[120px] bg-white">Date</TableHead>
                    <TableHead className="bg-white">Customer</TableHead>
                    <TableHead className="w-[110px] bg-white">Payment</TableHead>
                    <TableHead className="text-right w-[80px] bg-white">Items</TableHead>
                    <TableHead className="text-right w-[130px] bg-white">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentBills.map((b) => (
                    <TableRow
                      key={b.id || b.bill_no}
                      className="cursor-pointer hover:bg-muted/50 transition-colors data-row"
                      onClick={() => setPreviewBill(b)}
                      data-testid={`recent-bill-row-${b.bill_no}`}
                    >
                      <TableCell className="font-mono text-xs font-medium text-foreground py-2.5">
                        <div className="flex items-center gap-1.5">
                          <Printer size={12} className="text-muted-foreground" />
                          <span>{b.bill_no}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap py-2.5">
                        {formatDate(b.created_at)}
                      </TableCell>
                      <TableCell className="text-sm font-medium text-foreground py-2.5">
                        {b.customer_name || "Walk-in Customer"}
                      </TableCell>
                      <TableCell className="py-2.5">
                        <PaymentBadge mode={b.payment_mode} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-muted-foreground py-2.5">
                        {b.items?.length || 0}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm font-semibold text-foreground py-2.5">
                        {inr(b.grand_total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
        <div className="border-t border-border px-5 py-2.5 bg-muted/20 flex justify-end shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-primary font-medium hover:text-primary/80 hover:bg-transparent p-0 h-auto gap-1"
            onClick={() => navigate("/reports?tab=sales")}
            data-testid="view-all-sales-footer"
          >
            View All Sales Bills <ArrowRight size={13} />
          </Button>
        </div>
      </Card>

      {/* Dialogs: Invoice Preview and Delete Confirmation */}
      <InvoicePreviewDialog
        open={!!previewBill}
        onOpenChange={(o) => !o && setPreviewBill(null)}
        bill={previewBill}
        shop={shopInfo}
        onDeleteBill={canDeleteBills ? openDeleteModal : undefined}
        onPaymentUpdated={handlePaymentUpdated}
      />

      <DeleteBillDialog
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        bill={billToDelete}
        onSuccess={handleBillDeleted}
      />
    </div>
  );
}
